import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Show } from "@clerk/react";
import { toast } from "sonner";
import { CONTENT } from "@/content";
import { errorText, useApi, useMe } from "@/lib/api";
import { ContentForm } from "@/components/admin/ContentForm";
import { ContentList } from "@/components/admin/ContentList";
import { UsersPanel } from "@/components/admin/UsersPanel";
import { SettingsForm } from "@/components/admin/SettingsForm";
import { Ripples } from "@/components/admin/Ripples";

const A = CONTENT.admin;

const ContentManager = () => {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => api("get", "/admin/content").then(setItems).catch((e) => toast.error(errorText(e))), [api]);
  useEffect(() => { load(); }, [load]);

  const remove = async (item) => {
    try { await api("delete", `/admin/content/${item.id}`); toast.success(A.deleted); load(); }
    catch (e) { toast.error(errorText(e)); }
  };

  if (editing) return <ContentForm item={editing === "new" ? null : editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
  const shown = items && items.filter((i) => i.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <div className="admin-bar">
        <h2 className="admin-h2">{A.contentTitle}</h2>
        <button className="nb-btn" onClick={() => setEditing("new")} data-testid="admin-add-content-button">{A.newContent}</button>
      </div>
      <input className="nb-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={A.searchPlaceholder} data-testid="admin-content-search" />
      <ContentList items={shown} onEdit={setEditing} onDelete={remove} />
    </>
  );
};

const SCREENS = {
  content: () => <ContentManager />,
  prayer: () => <SettingsForm section="prayer_card" form={A.forms.prayer} />,
  thankyou: () => <SettingsForm section="thank_you" form={A.forms.thankyou} />,
  settings: () => <SettingsForm section="site" form={A.forms.settings} />,
  ripples: () => <Ripples />,
  users: () => <UsersPanel />,
};

const AdminArea = () => {
  const { loading, isAdmin } = useMe();
  const [tab, setTab] = useState("content");
  if (loading) return <p className="muted" data-testid="admin-loading">{A.loading}</p>;
  if (!isAdmin) return <div className="nb-card" data-testid="admin-not-allowed"><p className="panel-line">{A.notAllowed}</p></div>;
  return (
    <div className="admin-layout">
      <nav className="admin-menu" data-testid="admin-menu">
        {Object.keys(SCREENS).map((t) => (
          <button key={t} className={`tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}>{A.menu[t]}</button>
        ))}
      </nav>
      <div className="admin-main">{SCREENS[tab]()}</div>
    </div>
  );
};

export default function Admin() {
  return (
    <main className="page admin" data-testid="admin-page">
      <h1 className="admin-title">{A.title}</h1>
      <Show when="signed-in"><AdminArea /></Show>
      <Show when="signed-out"><Navigate to="/account" replace /></Show>
    </main>
  );
}
